import { useCallback, useEffect, useRef } from 'react';
import {
  createResolvedFieldSelectionHandler as createSharedResolvedFieldSelectionHandler,
  replaceRowWithFieldValueBatch,
  replaceRowWithRowBatch
} from '@/lib/table-handlers';
import { type TableSelectedCell, useTableRows } from '@/components/custom/erp/input-table-v2';
import { calculateRowValues, roundToOneDecimal, toNumber } from '../utils/row-calculation';

export type SelectedCellInfo = TableSelectedCell;

export interface DetailRow {
  uuid: string;
  line?: number;
  so_luong?: number;
  ma_vt_data?: BanHangRecord;
  dvt?: BanHangRecord;
  tl_ck?: number;
  ck_nt?: number;
  ma_thue_data?: {
    thue_suat?: number;
  };
  [key: string]: BanHangValue;
}
type PriceLookupMeta = {
  isInitializing: boolean;
};

type PriceLookupHandler = (
  row: DetailRow,
  field: string,
  value: BanHangValue,
  updateRow: (rowUuid: string, fields: Partial<DetailRow>) => void,
  meta: PriceLookupMeta
) => void;

const ensureRowDefaults = <T extends DetailRow>(row: Partial<T>, index: number): T =>
  ({
    ...row,
    uuid: row?.uuid && row.uuid !== '' ? row.uuid : `temp-${Date.now()}-${index}`,
    ct_km: row?.ct_km ?? '0',
    ...(row?.line !== undefined && { line: row.line || index + 1 })
  }) as unknown as T;

const normalizeInitialRows = <T extends DetailRow>(rows: T[] = []): T[] => {
  if (rows.length > 0) {
    return rows.map((row, index) => ensureRowDefaults(row, index)) as T[];
  }

  return [
    ensureRowDefaults(
      {
        uuid: `temp-${Date.now()}`,
        line: 1
      } as T,
      0
    )
  ] as T[];
};

const hasFieldChanges = (row: DetailRow, fields: Partial<DetailRow>) =>
  Object.entries(fields).some(([field, value]) => !Object.is(row[field], value));

const createFieldUpdates = (
  row: DetailRow,
  field: string,
  value: BanHangValue,
  loai_ck?: string | number,
  ck_tl_nt?: number
): Partial<DetailRow> => {
  const updatedRow: DetailRow = { ...row, [field]: value };
  const so_luong = toNumber(field === 'so_luong' ? value : updatedRow.so_luong);
  const updates: Partial<DetailRow> = { [field]: value };

  const resolveDvtData = (maVtData: BanHangRecord | undefined, dvtValue: BanHangValue) => {
    if (!maVtData || !dvtValue) {
      return null;
    }

    const convertedUnit = maVtData.quy_doi_dvt_data?.find((item: BanHangRecord) => item.dvt === dvtValue);
    return convertedUnit?.dvt_data || maVtData.dvt_nl_data || maVtData.dvt_data || null;
  };

  if (field === 'gia_nt2' || field === 'so_luong') {
    const giaBan = toNumber(field === 'gia_nt2' ? value : updatedRow.gia_nt2);
    const tien = roundToOneDecimal(so_luong * giaBan);
    updates.tien_nt2 = tien;
    updates.tien_tck_nt = tien;
  }
  if (field === 'ma_vt_data') {
    const nextDvt = value?.dvt_nl_data?.uuid || value?.dvt_data?.uuid || value?.dvt_nl || value?.dvt || null;
    updates.dvt = nextDvt;
    updates.dvt_data = resolveDvtData(value, nextDvt);
  }

  if (field === 'gia_nt') {
    const giaVon = toNumber(value);
    updates.tien_nt = roundToOneDecimal(so_luong * giaVon);
  }

  if (field === 'tien_nt2') {
    const tien = toNumber(value);
    updates.tien_nt2 = tien;
    updates.tien_tck_nt = tien;
    if (so_luong) {
      updates.gia_nt2 = roundToOneDecimal(tien / so_luong);
    }
  }

  if (field === 'tien_tck_nt') {
    const tien = toNumber(value);
    updates.tien_tck_nt = tien;
    updates.tien_nt2 = tien;
  }

  if (field === 'dvt') {
    updates.dvt_data = resolveDvtData(updatedRow.ma_vt_data, value);
  }

  if (field === 'ma_vt_data') {
    updates.tk_dt_data = value?.tk_dt_data || null;
    updates.tk_gv_data = value?.tk_gv_data || null;
    updates.tk_vt_data = value?.tk_khcp_data || null;
    updates.tk_ck_data = value?.tk_ck_data || null;
    updates.tk_km_data = value?.tk_km_data || null;
    updates.tk_tl_data = value?.tk_tl_data || null;
  }
  if (field === 'ma_thue_data') {
    updates.tk_thue_co_data = value?.tk_thue_dau_ra_data || null;
  }

  const discountAndTax = calculateRowValues(
    { ...updatedRow, ...updates },
    loai_ck,
    ck_tl_nt,
    field === 'ck_nt' ? 'ck_nt' : field
  );

  return {
    ...updates,
    ...discountAndTax
  };
};

export function useDetailRows(
  initialRows: DetailRow[] = [],
  loai_ck?: string | number,
  ck_tl_nt?: number,
  onChange?: (rows: DetailRow[]) => void,
  priceLookupHandler?: PriceLookupHandler
) {
  const normalizedRows = normalizeInitialRows(initialRows);
  const priceLookupHandlerRef = useRef<PriceLookupHandler | undefined>(priceLookupHandler);
  const isInitializingRef = useRef(true);
  useEffect(() => {
    priceLookupHandlerRef.current = priceLookupHandler;
  }, [priceLookupHandler]);
  useEffect(() => {
    isInitializingRef.current = false;
  }, []);
  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange: baseHandleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<DetailRow>({
    initialRows: normalizedRows,
    onChange,
    onFieldValueChange: (row, field, value) => createFieldUpdates(row, field, value, loai_ck, ck_tl_nt),
    defaultField: 'dien_giai'
  });
  const updateRowByUuid = useCallback(
    (uuid: string, fields: Partial<DetailRow>) => {
      setRows(prev =>
        prev.map(row => {
          if (row.uuid !== uuid) return row;
          if (!hasFieldChanges(row, fields)) return row;
          return { ...row, ...fields };
        })
      );
    },
    [setRows]
  );

  const handleCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: BanHangValue) => {
      baseHandleCellValueChange(rowUuid, field, newValue);

      const lookupHandler = priceLookupHandlerRef.current;
      if (lookupHandler) {
        const row = rows.find(r => r.uuid === rowUuid);
        if (row) {
          const updatedRow = { ...row, [field]: newValue };
          lookupHandler(updatedRow, field, newValue, updateRowByUuid, {
            isInitializing: isInitializingRef.current
          });
        }
      }
    },
    [baseHandleCellValueChange, rows, updateRowByUuid]
  );

  const replaceRowWithRows = useCallback(
    (anchorRowUuid: string, nextRows: Partial<DetailRow>[], options?: { mergeWithAnchor?: boolean }) => {
      let nextSelectedRowUuid: string | null = null;

      setRows(prevRows => {
        const { rows: updatedRows, insertedRowUuids } = replaceRowWithRowBatch(prevRows, {
          anchorRowUuid,
          nextRows,
          mergeWithAnchor: options?.mergeWithAnchor ?? false
        });

        nextSelectedRowUuid = insertedRowUuids[0] ?? null;
        return updatedRows.map((row, index) => ensureRowDefaults(row, index));
      });

      if (nextSelectedRowUuid) {
        setSelectedRowUuid(nextSelectedRowUuid);
        setSelectedCell(null);
      }
    },
    [setRows, setSelectedCell, setSelectedRowUuid]
  );

  const replaceRowWithFieldValues = useCallback(
    (anchorRowUuid: string, field: string, values: BanHangRecord[]) => {
      let nextSelectedRowUuid: string | null = null;

      setRows(prevRows => {
        const { rows: updatedRows, insertedRowUuids } = replaceRowWithFieldValueBatch(prevRows, {
          anchorRowUuid,
          field,
          values,
          createFields: (anchorRow, changedField, value) =>
            createFieldUpdates(anchorRow, changedField, value, loai_ck, ck_tl_nt),
          normalizeRow: ensureRowDefaults
        });

        nextSelectedRowUuid = insertedRowUuids[0] ?? null;
        return updatedRows;
      });

      if (nextSelectedRowUuid) {
        setSelectedRowUuid(nextSelectedRowUuid);
        setSelectedCell(null);
      }
    },
    [ck_tl_nt, loai_ck, setRows, setSelectedCell, setSelectedRowUuid]
  );

  const createResolvedFieldSelectionHandler = useCallback(
    <TItem>({
      field,
      resolveValue
    }: {
      field: string;
      resolveValue: (item: TItem, index: number) => Promise<BanHangRecord>;
    }) =>
      createSharedResolvedFieldSelectionHandler({
        field,
        resolveValue,
        onSingleValue: handleCellValueChange,
        onMultipleValues: replaceRowWithFieldValues
      }),
    [handleCellValueChange, replaceRowWithFieldValues]
  );

  const setPriceLookupHandler = useCallback((handler?: PriceLookupHandler) => {
    priceLookupHandlerRef.current = handler;
  }, []);

  useEffect(() => {
    if (!loai_ck || ck_tl_nt === undefined || ck_tl_nt === null) return;
    setRows(prev =>
      prev.map(row => ({
        ...row,
        ...calculateRowValues(row, loai_ck, ck_tl_nt)
      }))
    );
  }, [loai_ck, ck_tl_nt, setRows]);

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    createResolvedFieldSelectionHandler,
    replaceRowWithFieldValues,
    replaceRowWithRows,
    setSelectedRowUuid,
    setSelectedRow,
    setSelectedCell,
    setNonWritableFields,
    setPasteConfigs,
    isPasting,
    setPriceLookupHandler
  };
}
